import { useMemo } from 'react';
import { defaultLetterheadBase64 } from '@/lib/pdf/default-letterhead-base64';
import type { LetterheadConfig } from '@/schemas/schema';
import { useAuthStore } from '@/state/store/auth';
import { useCurrentOrgId } from '@/store/use-my-oganization-store';
import { useGetOrgLetterheads } from './use-org-letterheads';

// Fallback letterhead used when the org has no UDS letterhead configured.
// Prefer the env-configured URL (VITE_DEFAULT_LETTERHEAD_URL) so it can be
// swapped without a code change; fall back to the bundled base64 PNG.
const HARDCODED_DEFAULT_LETTERHEAD: LetterheadConfig = {
  imageDataUrl:
    (import.meta.env.VITE_DEFAULT_LETTERHEAD_URL as string | undefined) ||
    defaultLetterheadBase64,
  pageWidth: 612,
  pageHeight: 792,
  contentArea: {
    x: 73,
    y: 87.12,
    width: 465,
    height: 612.88,
  },
};

interface UseResolvedDefaultLetterheadResult {
  resolvedLetterhead: LetterheadConfig;
  isLoading: boolean;
}

/**
 * Resolves the effective default letterhead.
 *
 * UDS stores letterhead dimensions as IMAGE PIXELS at 300 DPI (e.g.
 * 2480×3509 for an A4 letterhead), and the same is true for the
 * contentArea fields — they're pixel offsets/sizes within the image.
 * `@react-pdf/renderer` interprets the same values as PDF POINTS (1/72
 * inch). If we hand it 2480×3509 unchanged, it tries to rasterize a
 * 34"×49" surface on the main thread and Chrome flags the tab
 * "Page Unresponsive."
 *
 * The fix is a proportional conversion (×72/300 = 0.24). pageWidth/
 * pageHeight become the actual A4 in points, contentArea margins follow
 * the same ratio, and the renderer sees a normal-sized page.
 *
 * Note on margins: the margins are whatever the letterhead designer
 * stored. A 50-pixel margin at 300 DPI = 12pt = 0.17". If the org wants
 * wider margins in the rendered PDF, the fix is to update the org
 * letterhead's contentArea on UDS (e.g., x=73, y=96, width=2334,
 * height=2966 → 1-inch margins).
 */
export const useResolvedDefaultLetterhead = (): UseResolvedDefaultLetterheadResult => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const orgId = useCurrentOrgId();
  const { data: orgLetterheadsData, isLoading } = useGetOrgLetterheads(
    isAuthenticated ? (orgId ?? undefined) : undefined
  );

  const resolvedLetterhead = useMemo<LetterheadConfig>(() => {
    if (!isAuthenticated || !orgLetterheadsData?.items?.length) {
      return HARDCODED_DEFAULT_LETTERHEAD;
    }

    const orgDefault =
      orgLetterheadsData.items.find((lh) => lh.IsDefault) || orgLetterheadsData.items[0];
    if (!orgDefault?.FileUrl) return HARDCODED_DEFAULT_LETTERHEAD;

    // Detect pixel-stored dimensions by magnitude. No standard PDF page is
    // wider than ~1500 points (A0 is 2384); anything bigger is image
    // pixels at 300 DPI. Convert at 72/300 = 0.24 pt per pixel.
    const POINTS_PER_PIXEL_AT_300_DPI = 72 / 300;
    const isStoredInPixels = (orgDefault.PageWidth ?? 0) > 1500;
    const toPt = (v: number | undefined | null, fallback: number): number =>
      v == null ? fallback : isStoredInPixels ? v * POINTS_PER_PIXEL_AT_300_DPI : v;

    return {
      imageDataUrl: orgDefault.FileUrl,
      pageWidth: toPt(orgDefault.PageWidth, 612),
      pageHeight: toPt(orgDefault.PageHeight, 792),
      contentArea: {
        x: toPt(orgDefault.ContentAreaX, 73),
        y: toPt(orgDefault.ContentAreaY, 87.12),
        width: toPt(orgDefault.ContentAreaWidth, 465),
        height: toPt(orgDefault.ContentAreaHeight, 612.88),
      },
    };
  }, [isAuthenticated, orgLetterheadsData]);

  return {
    resolvedLetterhead,
    isLoading: isAuthenticated ? isLoading : false,
  };
};
